import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import ApiClient from '../../utils/ApiClient';

// Stock-aware Sponsored Products: advertise only SKUs with sellable NEW units — FBA, plus SFP / MER merchant listings.
// The server builds the plan from live Ads API state + today's AFN inventory report, and re-checks
// every item on apply — nothing is sent to Amazon until the confirm dialog is accepted.

const SECTIONS = [
    { key: 'pause', label: 'Pause', hint: 'Running, but no sellable new stock', tone: 'red', selectable: true },
    { key: 'enable', label: 'Turn back on', hint: 'Paused, and back in stock', tone: 'green', selectable: true },
    { key: 'add', label: 'Add ads', hint: 'In stock, not advertised', tone: 'blue', selectable: true },
    { key: 'keep', label: 'Running & in stock', hint: 'No change needed', tone: 'gray', selectable: false },
];
const TONES = {
    red: 'border-red-500 text-red-700',
    green: 'border-green-500 text-green-700',
    blue: 'border-blue-500 text-blue-700',
    gray: 'border-gray-400 text-gray-700',
};
const THEMES = { CLOSE_MATCH: 'Close match', LOOSE_MATCH: 'Loose match', SUBSTITUTES: 'Substitutes', COMPLEMENTS: 'Complements' };
const LEVELS = ['Low', 'Suggested', 'High'];
const PAGE = 200;

const money = (v) => (v === null || v === undefined ? '—' : `$${Number(v).toFixed(2)}`);
const rowKey = (section, r) => (section === 'add' ? r.sku : r.adId);

function Modal({ title, children, onCancel, onConfirm, confirmLabel, busy, danger }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{title}</h3>
                <div className="text-sm text-gray-700 space-y-2">{children}</div>
                <div className="mt-6 flex justify-end gap-2">
                    <button onClick={onCancel} disabled={busy} className="px-4 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50">Cancel</button>
                    <button
                        onClick={onConfirm}
                        disabled={busy}
                        className={`px-4 py-2 text-sm rounded-md text-white disabled:opacity-50 ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                        {busy ? 'Working…' : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

function BidPanel({ target, disabled, onChanged }) {
    const [bid, setBid] = useState('');
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);

    if (!target) return null;
    const recs = target.bidRecommendations?.[0];
    const themes = recs?.bidRecommendationsForTargetingExpressions || [];
    const clicks = recs?.impactMetrics?.clicks?.values || [];
    const orders = recs?.impactMetrics?.orders?.values || [];
    const range = (v) => (v ? `${v.lower.toLocaleString()}–${v.upper.toLocaleString()}` : '—');
    const parsed = Number(bid);
    const valid = bid !== '' && parsed >= 0.02 && parsed <= 5;

    const submit = async () => {
        setBusy(true);
        setMsg(null);
        try {
            const res = await ApiClient.post('/api/amazon-ads/default-bid', { adGroupId: target.adGroupId, bid: parsed });
            setMsg({ ok: true, text: `Default bid changed from ${money(res.data.oldBid)} to ${money(res.data.newBid)}.` });
            setBid('');
            onChanged();
        } catch (err) {
            setMsg({ ok: false, text: err.response?.data?.error || 'Bid change failed.' });
        } finally {
            setBusy(false);
            setConfirming(false);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h2 className="text-sm font-semibold text-gray-700">Bid — {target.campaignName} / {target.adGroupName}</h2>
                    <p className="text-sm text-gray-600 mt-1">
                        Default bid <span className="font-semibold text-gray-900">{money(target.defaultBid)}</span> per click ·
                        budget {money(target.dailyBudget)}/day · {target.biddingStrategy || '—'}. New ads are added here.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">New bid $</span>
                    <input
                        type="number" step="0.01" min="0.02" max="5" value={bid}
                        onChange={(e) => setBid(e.target.value)}
                        className="w-24 border border-gray-300 rounded-md px-2 py-1 text-sm"
                        placeholder="0.42"
                    />
                    <button
                        onClick={() => setConfirming(true)}
                        disabled={!valid || disabled}
                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                        Set bid
                    </button>
                </div>
            </div>
            {themes.length > 0 ? (
                <div className="overflow-x-auto mt-3">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-3 py-2 text-left font-medium text-gray-600">Amazon's suggested bid</th>
                                {LEVELS.map((l) => <th key={l} className="px-3 py-2 text-right font-medium text-gray-600">{l}</th>)}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {themes.map((t) => (
                                <tr key={t.targetingExpression.type}>
                                    <td className="px-3 py-1.5 text-gray-700">{THEMES[t.targetingExpression.type] || t.targetingExpression.type}</td>
                                    {t.bidValues.map((b, i) => (
                                        <td key={i} className="px-3 py-1.5 text-right tabular-nums">
                                            <button className="text-blue-600 hover:underline" onClick={() => setBid(String(b.suggestedBid))}>{money(b.suggestedBid)}</button>
                                        </td>
                                    ))}
                                </tr>
                            ))}
                            <tr className="text-gray-500">
                                <td className="px-3 py-1.5">Amazon's estimate: clicks</td>
                                {LEVELS.map((l, i) => <td key={l} className="px-3 py-1.5 text-right tabular-nums">{range(clicks[i])}</td>)}
                            </tr>
                            <tr className="text-gray-500">
                                <td className="px-3 py-1.5">Amazon's estimate: orders</td>
                                {LEVELS.map((l, i) => <td key={l} className="px-3 py-1.5 text-right tabular-nums">{range(orders[i])}</td>)}
                            </tr>
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className="text-xs text-gray-500 mt-2">Amazon returned no bid suggestions{target.bidRecommendationsError ? `: ${target.bidRecommendationsError}` : '.'}</p>
            )}
            {msg && <div className={`mt-3 text-sm ${msg.ok ? 'text-green-700' : 'text-red-700'}`}>{msg.text}</div>}
            {confirming && (
                <Modal
                    title="Change the default bid?"
                    confirmLabel={`Set ${money(parsed)}`}
                    busy={busy}
                    danger={parsed > Number(target.defaultBid)}
                    onCancel={() => setConfirming(false)}
                    onConfirm={submit}
                >
                    <p>
                        <span className="font-medium">{target.adGroupName}</span> in <span className="font-medium">{target.campaignName}</span>:
                        {' '}{money(target.defaultBid)} → <span className="font-semibold">{money(parsed)}</span> per click.
                    </p>
                    <p>This applies to every product in the ad group immediately. Spend is still capped by the {money(target.dailyBudget)}/day budget.</p>
                </Modal>
            )}
        </div>
    );
}

function StockAwareAds() {
    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [section, setSection] = useState('pause');
    const [excluded, setExcluded] = useState({ pause: new Set(), enable: new Set(), add: new Set() });
    const [search, setSearch] = useState('');
    const [confirming, setConfirming] = useState(false);
    const [applying, setApplying] = useState(false);
    const [result, setResult] = useState(null);
    const [progress, setProgress] = useState(null);
    const [log, setLog] = useState(null);

    const load = useCallback(async () => {
        setError(null);
        try {
            const res = await ApiClient.get('/api/amazon-ads/stock-plan');
            setPlan(res.data);
            setApplying(res.data.applying);
            setProgress(res.data.progress || null);
            // The run lives on the server: coming back to the page shows where it got to.
            if (!res.data.applying && res.data.lastApply) setResult(res.data.lastApply);
            setExcluded({ pause: new Set(), enable: new Set(), add: new Set() });
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load the stock plan.');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadLog = useCallback(async () => {
        try {
            const res = await ApiClient.get('/api/amazon-ads/change-log?limit=200');
            setLog(res.data);
        } catch (err) {
            setLog([]);
        }
    }, []);

    useEffect(() => { load(); loadLog(); }, [load, loadLog]);

    // While the server works through batches, poll; reload the plan when it's done.
    useEffect(() => {
        if (!applying) return undefined;
        const t = setInterval(async () => {
            try {
                const res = await ApiClient.get('/api/amazon-ads/stock-plan/status');
                setProgress(res.data.progress || null);
                if (!res.data.applying) {
                    setApplying(false);
                    setResult(res.data.lastApply);
                    setLoading(true);
                    load();
                    loadLog();
                }
            } catch (err) { /* keep polling */ }
        }, 5000);
        return () => clearInterval(t);
    }, [applying, load, loadLog]);

    const selected = useMemo(() => {
        if (!plan) return { pause: [], enable: [], add: [] };
        const pick = (k) => plan[k].filter((r) => !excluded[k].has(rowKey(k, r))).map((r) => rowKey(k, r));
        return { pause: pick('pause'), enable: pick('enable'), add: pick('add') };
    }, [plan, excluded]);
    const totalSelected = selected.pause.length + selected.enable.length + selected.add.length;

    const rows = useMemo(() => {
        if (!plan) return [];
        const q = search.trim().toLowerCase();
        const list = plan[section] || [];
        if (!q) return list;
        return list.filter((r) => [r.sku, r.asin, r.title].some((v) => v && v.toLowerCase().includes(q)));
    }, [plan, section, search]);

    const toggle = (key) => setExcluded((ex) => {
        const next = new Set(ex[section]);
        if (next.has(key)) next.delete(key); else next.add(key);
        return { ...ex, [section]: next };
    });
    const setAll = (include) => setExcluded((ex) => ({
        ...ex, [section]: include ? new Set() : new Set((plan[section] || []).map((r) => rowKey(section, r))),
    }));

    const apply = async () => {
        setResult(null);
        try {
            await ApiClient.post('/api/amazon-ads/stock-plan/apply', {
                pauseAdIds: selected.pause, enableAdIds: selected.enable, addSkus: selected.add,
            });
            setApplying(true);
        } catch (err) {
            setError(err.response?.data?.error || 'Could not start the update.');
        } finally {
            setConfirming(false);
        }
    };

    if (loading) return <div className="text-gray-500">Loading live ads and stock… (reads every ad from Amazon, takes a few seconds)</div>;
    if (!plan) return error ? <div className="rounded-md bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-800">{error}</div> : null;

    const meta = SECTIONS.find((s) => s.key === section);
    const sectionSelected = meta.selectable ? plan[section].length - excluded[section].size : 0;

    return (
        <div>
            {error && <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-800">{error}</div>}
            {plan.stockStale && (
                <div className="mb-4 flex items-center gap-2 rounded-md bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-800">
                    <ExclamationTriangleIcon className="h-5 w-5" />
                    Stock data is stale (FBA {plan.stockAsOf || 'never'}, SFP/MER {plan.mfnStockAsOf || 'never'}) — an inventory report hasn't loaded. Changes are blocked until it does.
                </div>
            )}
            {applying && (
                <div className="mb-4 rounded-md bg-blue-50 border border-blue-200 px-4 py-2 text-sm text-blue-800 flex items-center gap-2">
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    Sending changes to Amazon on the server
                    {progress && ` — ${progress.done.toLocaleString()} of ${progress.total.toLocaleString()} sent${progress.startedBy ? ` (started by ${progress.startedBy})` : ''}`}.
                    You can leave this page; it keeps running.
                </div>
            )}
            {result && (
                <div className={`mb-4 rounded-md border px-4 py-2 text-sm ${result.error || result.errorCount ? 'bg-yellow-50 border-yellow-200 text-yellow-900' : 'bg-green-50 border-green-200 text-green-800'}`}>
                    {result.error ? `Update failed: ${result.error}` : (
                        <>
                            Last update{result.finishedAt && ` (${String(result.finishedAt).replace('T', ' ').slice(0, 16)}${result.startedBy ? `, ${result.startedBy}` : ''})`}:
                            {' '}paused {result.paused.ok}, turned on {result.enabled.ok}, added {result.added.ok}.
                            {result.errorCount > 0 && ` ${result.errorCount} rejected by Amazon.`}
                            {result.skipped > 0 && ` ${result.skipped} skipped (no longer applied when re-checked).`}
                            {result.errors?.length > 0 && <ul className="mt-1 list-disc ml-5">{result.errors.map((e) => <li key={e}>{e}</li>)}</ul>}
                        </>
                    )}
                </div>
            )}

            <p className="text-sm text-gray-600 mb-4">
                In stock = any sellable <span className="font-medium">new</span> units: FBA ({plan.inStockFbaSkus} SKUs, as of {plan.stockAsOf})
                or an active new-condition SFP / MER merchant listing ({plan.inStockMfnSkus} SKUs, as of {plan.mfnStockAsOf}).
                Used units can't be advertised — Sponsored Products only serves new-condition offers.
            </p>

            <BidPanel target={plan.target} disabled={applying} onChanged={() => { load(); loadLog(); }} />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {SECTIONS.map((s) => (
                    <button
                        key={s.key}
                        onClick={() => { setSection(s.key); setSearch(''); }}
                        className={`text-left bg-white rounded-lg shadow p-4 border-b-4 ${section === s.key ? TONES[s.tone] : 'border-transparent'}`}
                    >
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{s.label}</div>
                        <div className="mt-1 text-2xl font-semibold text-gray-900 tabular-nums">{plan[s.key].length.toLocaleString()}</div>
                        <div className="text-xs text-gray-500 mt-1">{s.hint}</div>
                    </button>
                ))}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="text-sm text-gray-700">
                    Selected: pause <b>{selected.pause.length}</b> · turn on <b>{selected.enable.length}</b> · add <b>{selected.add.length}</b>
                </div>
                <button
                    onClick={() => setConfirming(true)}
                    disabled={totalSelected === 0 || applying || plan.stockStale}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                    Review &amp; apply {totalSelected.toLocaleString()} changes
                </button>
            </div>

            <div className="bg-white rounded-lg shadow mb-6">
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-200">
                    <h2 className="text-sm font-semibold text-gray-700">
                        {meta.label} ({rows.length.toLocaleString()}){meta.selectable && ` · ${sectionSelected.toLocaleString()} selected`}
                    </h2>
                    <div className="flex items-center gap-2">
                        {meta.selectable && (
                            <>
                                <button onClick={() => setAll(true)} className="text-xs text-blue-600 hover:underline">Select all</button>
                                <button onClick={() => setAll(false)} className="text-xs text-blue-600 hover:underline">Select none</button>
                            </>
                        )}
                        <input
                            value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search SKU, ASIN, title"
                            className="text-sm border border-gray-300 rounded-md px-2 py-1 w-56"
                        />
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                {meta.selectable && <th className="px-3 py-2 w-8" />}
                                <th className="px-3 py-2 text-left font-medium text-gray-600">SKU</th>
                                <th className="px-3 py-2 text-left font-medium text-gray-600">ASIN</th>
                                <th className="px-3 py-2 text-left font-medium text-gray-600">Title</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-600">Stock</th>
                                {section !== 'add' && <th className="px-3 py-2 text-left font-medium text-gray-600">Campaign</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {rows.slice(0, PAGE).map((r) => {
                                const key = rowKey(section, r);
                                return (
                                    <tr key={key} className="hover:bg-gray-50">
                                        {meta.selectable && (
                                            <td className="px-3 py-1.5">
                                                <input type="checkbox" checked={!excluded[section].has(key)} onChange={() => toggle(key)} />
                                            </td>
                                        )}
                                        <td className="px-3 py-1.5 font-mono text-xs text-gray-800">{r.sku}</td>
                                        <td className="px-3 py-1.5">
                                            {r.asin ? <a href={`https://amazon.com/dp/${r.asin}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{r.asin}</a> : '—'}
                                        </td>
                                        <td className="px-3 py-1.5 max-w-md truncate text-gray-700" title={r.title || ''}>{r.title || <span className="text-gray-400">not in listings report</span>}</td>
                                        <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">
                                            {r.qty}
                                            {r.channel && <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded ${r.channel === 'FBA' ? 'bg-orange-100 text-orange-800' : 'bg-purple-100 text-purple-800'}`}>{r.channel === 'FBA' ? 'FBA' : 'SFP/MER'}</span>}
                                        </td>
                                        {section !== 'add' && <td className="px-3 py-1.5 text-gray-600">{r.campaign}</td>}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {rows.length > PAGE && (
                        <div className="px-4 py-2 text-xs text-gray-500 border-t border-gray-100">
                            Showing {PAGE} of {rows.length.toLocaleString()} — search to narrow. Selection covers all rows, not just the ones shown.
                        </div>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-lg shadow">
                <div className="px-4 py-3 border-b border-gray-200 text-sm font-semibold text-gray-700">Change log (last 200)</div>
                {!log || log.length === 0 ? (
                    <div className="px-4 py-6 text-sm text-gray-500">No changes made from this page yet.</div>
                ) : (
                    <div className="overflow-x-auto max-h-96">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                    {['When', 'Who', 'Change', 'SKU', 'From → to', 'Stock', 'Result'].map((h) => (
                                        <th key={h} className="px-3 py-2 text-left font-medium text-gray-600">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {log.map((l, i) => (
                                    <tr key={i}>
                                        <td className="px-3 py-1.5 whitespace-nowrap text-gray-600">{String(l.changed_at).replace('T', ' ').slice(0, 16)}</td>
                                        <td className="px-3 py-1.5 text-gray-600">{l.changed_by || '—'}</td>
                                        <td className="px-3 py-1.5">{l.change_type}</td>
                                        <td className="px-3 py-1.5 font-mono text-xs">{l.sku || l.entity_id}</td>
                                        <td className="px-3 py-1.5 text-gray-600">{l.old_value || '—'} → {l.new_value}</td>
                                        <td className="px-3 py-1.5 tabular-nums">{l.fba_qty ?? '—'}</td>
                                        <td className={`px-3 py-1.5 ${l.success ? 'text-green-700' : 'text-red-700'}`}>{l.success ? 'OK' : l.error}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {confirming && (
                <Modal
                    title="Apply these changes on Amazon?"
                    confirmLabel={`Apply ${totalSelected.toLocaleString()} changes`}
                    onCancel={() => setConfirming(false)}
                    onConfirm={apply}
                >
                    <ul className="list-disc ml-5">
                        {selected.pause.length > 0 && <li>Pause <b>{selected.pause.length.toLocaleString()}</b> ads with no sellable new stock.</li>}
                        {selected.enable.length > 0 && <li>Turn on <b>{selected.enable.length.toLocaleString()}</b> paused ads that are back in stock.</li>}
                        {selected.add.length > 0 && plan.target && (
                            <li>
                                Add <b>{selected.add.length.toLocaleString()}</b> new ads to <b>{plan.target.campaignName}</b> / {plan.target.adGroupName} at
                                the {money(plan.target.defaultBid)} default bid.
                            </li>
                        )}
                    </ul>
                    <p>Each item is re-checked against current stock before it's sent; anything that no longer qualifies is skipped.</p>
                    <p>This runs on the server — you can close or leave the page once it starts.</p>
                </Modal>
            )}
        </div>
    );
}

export default StockAwareAds;
